<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import {
  patientServiceCategoriesApi,
  type PatientServiceCategory,
} from '@/api/patient-service-categories';

interface Props {
  patientServiceCategory: PatientServiceCategory;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

// Updating a discarded category undiscards it (with its subcategories) on the backend.
const undiscardMutation = useMutation({
  mutationFn: () => patientServiceCategoriesApi.update({ id: props.patientServiceCategory.id }),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  undiscardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Restaurar Categoría "{{ patientServiceCategory.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea restaurar la categoría "{{ patientServiceCategory.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La categoría volverá a estar disponible junto con sus subcategorías.
      </p>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="undiscardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="success"
        :loading="undiscardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Restaurar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="undiscardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
